<template>
  <div v-if="radarLabels.length >= 3" class="garden-sidebar__card">
    <div class="skill-map__topbar" />
    <h3 class="garden-sidebar__heading" style="margin-top: 0.75rem;">
      <MTIcon name="sparkles" :size="16" class="skill-map__icon" />
      Your Skill Map
    </h3>
    <div class="skill-map__chart">
      <Radar :data="radarData" :options="radarOptions" />
    </div>
  </div>
</template>

<script>
// Module-level registration — runs once when the module is first loaded,
// not on every component mount, avoiding repeated re-registration conflicts.
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);
</script>

<script setup>
import { computed } from 'vue';
import { Radar } from 'vue-chartjs';
import MTIcon from '@/components/ui/MTIcon.vue';
import { useProgressStore } from '@/stores/progressStore';
import { useCurriculumStore } from '@/stores/curriculumStore';

const props = defineProps({
  childId: { type: String, required: true }
});

const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();

const moduleAccuracies = computed(() => {
  const sgs = curriculumStore.allSectionGroups;
  const childProgress = progressStore.progress?.[props.childId] || {};
  const modMap = {};

  for (const sg of sgs) {
    if (!modMap[sg.moduleId]) {
      modMap[sg.moduleId] = { id: sg.moduleId, title: sg.moduleTitle, total: 0, sum: 0 };
    }
    const p = childProgress[sg.sectionGroupId];
    if (p && typeof p.accuracy === 'number') {
      modMap[sg.moduleId].total++;
      modMap[sg.moduleId].sum += p.accuracy;
    }
  }

  return Object.values(modMap)
    .filter(m => m.total > 0)
    .map(m => ({ ...m, accuracy: m.sum / m.total }));
});

const radarLabels = computed(() => moduleAccuracies.value.map(m => m.title));

const radarData = computed(() => ({
  labels: radarLabels.value,
  datasets: [{
    label: 'Accuracy',
    data: moduleAccuracies.value.map(m => Math.round(m.accuracy * 100)),
    backgroundColor: 'rgba(99, 102, 241, 0.15)',
    borderColor: 'rgba(99, 102, 241, 0.8)',
    borderWidth: 2.5,
    pointBackgroundColor: '#6366f1',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    pointRadius: 4,
    fill: true,
  }]
}));

const radarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    r: {
      beginAtZero: true,
      max: 100,
      ticks: { stepSize: 25, display: false },
      grid: { color: 'rgba(0,0,0,0.06)' },
      angleLines: { color: 'rgba(0,0,0,0.06)' },
      pointLabels: {
        font: { size: 11, weight: '600' },
        color: '#6b7280',
      }
    }
  },
  animation: { duration: 800, easing: 'easeOutQuart' }
};
</script>

<style scoped>
.skill-map__icon { color: #7c3aed; }
.skill-map__topbar {
  height: 4px;
  background: linear-gradient(to right, #6366f1, #a855f7);
  margin: -1.25rem -1.25rem 0;
  border-radius: 1rem 1rem 0 0;
}
.skill-map__chart { height: 220px; position: relative; }
</style>

<style>
.theme-space .skill-map__topbar { background: linear-gradient(to right, #6ee7b7, #a78bfa); }
</style>
